import get from "lodash/get";

/**
 * Check if a card has a specific attribute set to "true"
 * @param {Object} card - The card object
 * @param {string} attributeName - The attribute name to check
 * @returns {boolean}
 */
export const getCardAttribute = (card, attributeName) => {
  const cardAttributes = get(card, ["metadata", "attributes"], {});
  return get(cardAttributes, [attributeName, 0], "") === "true";
};

/**
 * Check if a card is an ad card
 * @param {Object} card - The card object
 * @returns {boolean}
 */
export const isAdCard = (card) => getCardAttribute(card, "adimage");

/**
 * Get the card type based on image attributes
 * @param {Object} card - The card object
 * @returns {string|null} - "left-image", "right-image", "multi-image", or null
 */
export const getCardType = (card) => {
  if (getCardAttribute(card, "leftimage")) return "left-image";
  if (getCardAttribute(card, "rightimage")) return "right-image";
  if (getCardAttribute(card, "multiimage")) return "multi-image";
  return null;
};

/**
 * Check if a card uses brand colour
 * @param {Object} card - The card object
 * @returns {boolean}
 */
export const usesBrandColour = (card) => getCardAttribute(card, "brandcolour1");

/**
 * Check if a card is a partnership card (title + image-gallery composite)
 * @param {Object} card - The card object
 * @returns {boolean}
 */
export const isPartnershipCard = (card) => {
  const storyElements = get(card, ["story-elements"], []);
  if (storyElements.length < 2) return false;

  const [firstElement, secondElement] = storyElements;

  const isTitle = get(firstElement, ["type"]) === "title";
  const isCompositeGallery = 
    get(secondElement, ["type"]) === "composite" && 
    get(secondElement, ["subtype"]) === "image-gallery";

  return isTitle && isCompositeGallery;
};

/**
 * Filter cards into ad cards and regular cards
 * @param {Array} cards - Array of card objects
 * @returns {{ adCards: Array, regularCards: Array }}
 */
export const filterCards = (cards = []) => {
  return cards.reduce(
    (acc, card) => {
      if (isAdCard(card)) {
        acc.adCards.push(card);
      } else {
        acc.regularCards.push(card);
      }
      return acc;
    },
    { adCards: [], regularCards: [] }
  );
};

/**
 * Get the position to insert ads based on total cards
 * @param {number} totalCards - Total number of cards
 * @returns {number|null} - Position to insert ads, or null if no ads should be inserted
 */
export const getAdInsertPosition = (totalCards) => {
  if (totalCards > 3) return 2;
  if (totalCards >= 1) return 1;
  return null;
};
